import React, { useMemo } from 'react';
import { LuChevronLeft, LuChevronRight, LuChevronsLeft, LuChevronsRight } from 'react-icons/lu';
import Select from './Select';

/**
 * Build truncated page number list: 1 2 3 … 12 13 14 … 434 435
 */
function buildPageItems(current, total) {
  if (total <= 1) return [1];
  if (total <= 9) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items = new Set([1, total, current]);
  for (let i = current - 1; i <= current + 1; i += 1) {
    if (i > 1 && i < total) items.add(i);
  }
  // Always show a window near the start/end when nearby
  if (current <= 4) {
    [2, 3, 4, 5].forEach((n) => { if (n < total) items.add(n); });
  }
  if (current >= total - 3) {
    [total - 4, total - 3, total - 2, total - 1].forEach((n) => {
      if (n > 1) items.add(n);
    });
  }

  const sorted = [...items].sort((a, b) => a - b);
  const result = [];
  let prev = 0;
  for (const page of sorted) {
    if (prev && page - prev > 1) result.push('…');
    result.push(page);
    prev = page;
  }
  return result;
}

/**
 * Modern enterprise pagination.
 *
 * Showing 61–80 of 8,682   Rows: 20 ▾   [‹] 1 2 3 … 434 435 [›]
 */
export default function Pagination({
  page,
  currentPage, // legacy alias
  pageSize = 20,
  total = 0,
  totalPages: totalPagesProp,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  loading = false,
  className = '',
  showPageSize = typeof onPageSizeChange === 'function',
  showRange = true,
}) {
  const totalPages = Math.max(
    1,
    totalPagesProp != null
      ? Number(totalPagesProp) || 1
      : Math.ceil(Math.max(0, total) / Math.max(1, pageSize)) || 1
  );
  const current = Math.min(Math.max(1, Number(page ?? currentPage) || 1), totalPages);
  const safeTotal = Math.max(0, Number(total) || 0);

  const rangeStart = safeTotal === 0 ? 0 : (current - 1) * pageSize + 1;
  const rangeEnd = safeTotal === 0 ? 0 : Math.min(current * pageSize, safeTotal);

  const items = useMemo(() => buildPageItems(current, totalPages), [current, totalPages]);

  if (safeTotal === 0 && totalPages <= 1) {
    return null;
  }

  return (
    <div className={`rt-pagination ${className}`.trim()} aria-label="Pagination" aria-busy={loading || undefined}>
      {showRange && (
        <div className="rt-pagination__range">
          {safeTotal > 0
            ? `Showing ${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${safeTotal.toLocaleString()}`
            : `Page ${current} of ${totalPages}`}
        </div>
      )}

      <div className="rt-pagination__controls">
        {showPageSize && typeof onPageSizeChange === 'function' && (
          <label className="rt-pagination__size">
            <span>Rows</span>
            <Select
              size="sm"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              aria-label="Rows per page"
              disabled={loading}
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </Select>
          </label>
        )}

        <button
          type="button"
          className="rt-pagination__btn"
          onClick={() => onPageChange?.(1)}
          disabled={current <= 1 || loading}
          aria-label="First page"
          title="First page"
        >
          <LuChevronsLeft size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="rt-pagination__btn"
          onClick={() => onPageChange?.(current - 1)}
          disabled={current <= 1 || loading}
          aria-label="Previous page"
          title="Previous page"
        >
          <LuChevronLeft size={15} aria-hidden="true" />
        </button>

        <div className="rt-pagination__pages">
          {items.map((item, idx) =>
            item === '…' ? (
              <span key={`e-${idx}`} className="rt-pagination__ellipsis" aria-hidden>
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`rt-pagination__page${item === current ? ' rt-pagination__page--active' : ''}`}
                onClick={() => onPageChange?.(item)}
                disabled={loading}
                aria-label={`Page ${item}`}
                aria-current={item === current ? 'page' : undefined}
              >
                {item}
              </button>
            )
          )}
        </div>

        <button
          type="button"
          className="rt-pagination__btn"
          onClick={() => onPageChange?.(current + 1)}
          disabled={current >= totalPages || loading}
          aria-label="Next page"
          title="Next page"
        >
          <LuChevronRight size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="rt-pagination__btn"
          onClick={() => onPageChange?.(totalPages)}
          disabled={current >= totalPages || loading}
          aria-label="Last page"
          title="Last page"
        >
          <LuChevronsRight size={15} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
